// Where an answer's drawings go in its prose.
//
// A table or chart belongs beside the paragraph that talks about it, and only
// the model knows which paragraph that is. So each drawing it makes comes with
// a MARKER — the render tools hand it over in their result — the model writes
// the marker on a line of its own where the drawing belongs, and the page and
// the dashboard card split the answer at the markers.
//
// One format, one file, imported by both sides: a marker the backend writes and
// the frontend cannot read is a stray `{{drawing:2}}` in front of a reader.

/** The marker the model writes where drawing `id` belongs. */
export function drawingMarker(id: string): string {
  return `{{drawing:${id}}}`;
}

/** What an id is made of — read by the marker regex and by the prefix check, so the two cannot disagree. */
const ID_CHARS = String.raw`\w-`;
const MARKER = new RegExp(String.raw`\{\{drawing:([${ID_CHARS}]+)\}\}`, 'g');
const NOT_ID_CHAR = new RegExp(`[^${ID_CHARS}]`);

// The marker's text around its id, taken from `drawingMarker` itself: `{{drawing:` and `}}`.
const [MARKER_HEAD, MARKER_TAIL] = drawingMarker('\u0000').split('\u0000') as [string, string];

/**
 * Whether `tail` can still grow into a marker: a prefix of `{{drawing:`, or all
 * of it followed by id characters and then less than the whole `}}`.
 */
function isMarkerPrefix(tail: string): boolean {
  if (!tail.startsWith(MARKER_HEAD)) return MARKER_HEAD.startsWith(tail);
  const rest = tail.slice(MARKER_HEAD.length);
  const idLength = rest.search(NOT_ID_CHAR);
  if (idLength === -1) return true;
  const closing = rest.slice(idLength);
  return idLength > 0 && closing.length < MARKER_TAIL.length && MARKER_TAIL.startsWith(closing);
}

/**
 * The answer without its markers: what the model is shown of its own earlier
 * answers, and what an eval grades. A marker is layout, not words.
 */
export function withoutMarkers(text: string): string {
  return text.replace(MARKER, '').replace(/\n{3,}/g, '\n\n').trim();
}

/** One piece of an answer, in reading order. `at` is the drawing's index in the message's list. */
export type AnswerPart<D> = { kind: 'text'; text: string } | { kind: 'drawing'; drawing: D; at: number };

/**
 * The answer in reading order: its prose, with each drawing where its marker
 * stands.
 *
 * `complete` is false while the answer is still arriving, and that changes two
 * things. A drawing nobody has placed yet is held back — its marker may still
 * be on its way, and a table that shows up at the bottom only to jump into the
 * prose is the layout this exists to end. And a marker cut off mid-stream
 * (`{{draw`) is held back rather than flashed as text; any other brace is prose.
 *
 * Once complete, a drawing without a marker — the model left it out, or the
 * thread was written before markers existed — follows all the prose.
 */
export function answerParts<D extends { id?: string }>(
  text: string,
  drawings: readonly D[],
  complete: boolean,
): AnswerPart<D>[] {
  const parts: AnswerPart<D>[] = [];
  const placed = new Set<number>();
  const pushText = (piece: string): void => {
    const trimmed = piece.trim();
    if (trimmed !== '') parts.push({ kind: 'text', text: trimmed });
  };

  // Only a trailing run that is still the start of a marker is held back — a
  // brace in prose or JSON streams through. A marker opens with two braces, so
  // the run starts at the last brace or the one before it.
  let open = text.lastIndexOf('{');
  if (open > 0 && text[open - 1] === '{') open -= 1;
  const held = !complete && open !== -1 && isMarkerPrefix(text.slice(open));
  const body = held ? text.slice(0, open) : text;

  const marker = new RegExp(MARKER.source, 'g');
  let from = 0;
  for (let match = marker.exec(body); match !== null; match = marker.exec(body)) {
    pushText(body.slice(from, match.index));
    from = match.index + match[0].length;
    const id = match[1];
    // The first of that id not already placed: a repeated marker places nothing twice.
    const at = drawings.findIndex((drawing, i) => drawing.id === id && !placed.has(i));
    if (at === -1) continue;
    placed.add(at);
    parts.push({ kind: 'drawing', drawing: drawings[at]!, at });
  }
  pushText(body.slice(from));

  if (complete) {
    drawings.forEach((drawing, at) => {
      if (!placed.has(at)) parts.push({ kind: 'drawing', drawing, at });
    });
  }
  return parts;
}
